import { Action, combineReducers, configureStore, Reducer } from '@reduxjs/toolkit';
import { PersistConfig, persistReducer, persistStore } from 'redux-persist';

import persistStorage from 'infrastructure/redux/store/persistStorage';

import mboAPI from 'infrastructure/redux/adapters/mboAPI/mboApi';
import { rtkQueryErrorLogger } from 'infrastructure/redux/adapters/mboAPI/rtkQueryErrorLogger';
import authSlice from 'infrastructure/redux/slices/auth.slice';
import bookingWizardSlice from 'infrastructure/redux/slices/bookingWizard.slice';
import discoverTournamentsSlice from 'infrastructure/redux/slices/discoverTournaments.slice';
import joinFlowSlice from 'infrastructure/redux/slices/joinFlow.slice';
import navigationHistorySlice from 'infrastructure/redux/slices/navigationHistory.slice';
import paymentSlice from 'infrastructure/redux/slices/payment.slice';
import playSlice from 'infrastructure/redux/slices/play.slice';
import referralSlice from 'infrastructure/redux/slices/referral.slice';
import remoteConfigSlice from 'infrastructure/redux/slices/remoteConfig.slice';
import rewardsSlice from 'infrastructure/redux/slices/rewards.slice';
import themeSlice from 'infrastructure/redux/slices/theme.slice';
import userSlice from 'infrastructure/redux/slices/user.slice';
import { getReduxStoreVersion, reset } from 'infrastructure/redux/store/actions';
import { createPlatformLogoutMiddleware } from 'infrastructure/redux/store/middleware';
import { purgePersistedAuthIfStale } from 'infrastructure/redux/store/purgePersistedAuth';

// Re-exported for existing importers; the source of truth now lives in
// store/actions to keep it out of the mboAPI require-cycle.
export { getReduxStoreVersion };

const persistConfig: PersistConfig<{
  key: string;
  storage: typeof persistStorage;
  whitelist: string[];
}> = {
  key: getReduxStoreVersion() || 'root',
  storage: persistStorage,
  blacklist: ['router', mboAPI.reducerPath, 'bookingForm'],
};

const appReducer = combineReducers({
  theme: themeSlice,
  bookingForm: bookingWizardSlice,
  auth: authSlice,
  paymentForm: paymentSlice,
  user: userSlice,
  rewards: rewardsSlice,
  play: playSlice,
  joinFlow: joinFlowSlice,
  navigationHistory: navigationHistorySlice,
  remoteConfig: remoteConfigSlice,
  referral: referralSlice,
  discoverTournaments: discoverTournamentsSlice,
  [mboAPI.reducerPath]: mboAPI.reducer,
});

// Root reducer that handles the reset action to clear state on logout
// This ensures RTK Query cache is cleared when switching users
// See: https://github.com/reduxjs/redux-toolkit/issues/1622
const rootReducer: Reducer<ReturnType<typeof appReducer>, Action> = (state, action) => {
  if (reset.match(action)) {
    const authState = state?.auth as { apiBaseURL?: string; selectedCountry?: string } | undefined;
    const apiBaseURL = authState?.apiBaseURL;
    const selectedCountry = authState?.selectedCountry;
    const remoteConfig = state?.remoteConfig;

    return appReducer(
      {
        auth: { apiBaseURL, selectedCountry },
        remoteConfig,
      } as unknown as ReturnType<typeof appReducer>,
      action,
    );
  }
  return appReducer(state, action);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const configureRootReducer: Reducer<any, Action<any>> = persistReducer(
  persistConfig,
  (state: RootState, action) => rootReducer(state, action),
);

const store = configureStore({
  reducer: configureRootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
    })
      .concat(mboAPI.middleware)
      .concat(rtkQueryErrorLogger)
      .concat(createPlatformLogoutMiddleware({ getPersistor: () => persistor, baseApi: mboAPI })),
});

// Web-only: gate on the build flag (like handleWebLogoutRedirect) rather than
// a thrown TypeError — on RN `window` aliases `global`, so a localStorage
// polyfill would otherwise silently enable this path on mobile.
if (process.env.VITE_IS_WEB && typeof window !== 'undefined') {
  purgePersistedAuthIfStale(window.localStorage, `persist:${getReduxStoreVersion()}`);
}

export const persistor = persistStore(store);
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export default store;
